/* eslint-disable react/button-has-type */
import { css } from '@emotion/react';
import cns from 'classnames';
import { BaseHTMLAttributes, forwardRef, MouseEventHandler } from 'react';
import { Flex, Box } from '../Layout';
import { Button } from '../Button/Button';
import { MapUiSize, MAPUI_SIZES } from '../../common/types';
import styled from '@emotion/styled';
import { space, SpaceProps } from 'styled-system';

export const ALERT_CLASSNAME = '__mapui_alert';

export type AlertVariant = 'info' | 'warning' | 'error' | 'success' | 'neutral';

export const ALERT_VARIANTS = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info',
  NEUTRAL: 'neutral',
};

export const ALERT_SIZES = MAPUI_SIZES;

const applyMainStyles =
  ({ variant, size }: Pick<AlertProps, 'variant' | 'size'>) =>
  (theme) => {
    const defaultStyle = css(theme.alert.defaultStyles);
    const sizeStyle = css(theme.alert[size]);
    const variantStyle = css(theme.alert[variant]);

    const styles = [defaultStyle, sizeStyle, variantStyle];

    return css(...styles);
  };

export interface AlertProps
  extends BaseHTMLAttributes<HTMLDivElement>,
    SpaceProps {
  className?: string;
  variant?: AlertVariant;
  size?: MapUiSize;
  isClosable?: boolean;
  onClose?: MouseEventHandler<HTMLButtonElement>;
  closeText?: string;
}

const AlertContainer = styled('div')<AlertProps>(
  ({ theme, variant = 'neutral', size = 'md' }) => ({
    ...applyMainStyles({ size, variant })(theme),
  }),
  ({ className }) => ({
    className: cns(ALERT_CLASSNAME, className),
  }),
  space
);

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      children,
      onClose,
      isClosable = false,
      closeText = 'Close',
      ...rest
    }: AlertProps,
    ref
  ) => (
    <AlertContainer {...rest} ref={ref}>
      <Flex alignItems="center">
        <Box flexGrow={4}>{children}</Box>
        {isClosable && (
          <Box>
            <Button variant="unstyled" size="xs" onClick={onClose}>
              {closeText}
            </Button>
          </Box>
        )}
      </Flex>
    </AlertContainer>
  )
);

Alert.displayName = 'Alert';
